'use client'

import { Session } from 'next-auth'
import AuthorizationModal from './Modal/AuthorizationModal'
import { AdditionalInfoModal } from './Modal'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AdditionalInfoType } from './Schema/AdditionalInfoSchema'
import { createMember, onboardProfile } from '@/app/web/actions'
import { toast } from '@/lib/utils/toast'
import { User } from '@/types'
import AuthorizationBanner from '../AuthorizationBanner'
import { useUserInfo } from '@/app/web/context/UserInfoContext'
import { fetchUserProfile } from './actions'

interface AuthorizationContainerProps {
  session?: Session | null
  userInfo: Partial<User> | null
}

const AuthorizationContainer = ({
  session,
  userInfo,
}: AuthorizationContainerProps) => {
  const [userProfile, setUserProfile] = useState<Partial<User> | null>(userInfo)

  // Check if user is hackathon-provisioned (incomplete profile: no firstName, lastName, countryOfResidence)
  const isHackathonProvisioned = session?.user && 
    !session.user.firstName && 
    !session.user.lastName && 
    !session.user.countryOfResidence

  const [showAuthorizationModal, setShowAuthorizationModal] = useState<boolean>(
    !!(session?.user && !userInfo && !isHackathonProvisioned)
  )
  const [showAdditionalInfoModal, setShowAdditionalInfoModal] =
    useState<boolean>(!!(session?.user && userInfo && !userInfo.onboardedAt))

  const [showAuthorizationBanner, setShowAuthorizationBanner] =
    useState<boolean>(!(userInfo && userInfo.onboardedAt) && !isHackathonProvisioned)

  const [isProfileComplete, setIsProfileComplete] = useState(false)

  const { setUserInfo } = useUserInfo()

  const handleConfirmation = useCallback(async () => {
    if (session?.user) {
      try {
        // Backend upserts by email/uuid, so this safely creates or updates the
        // profile (e.g. for users already provisioned during guest registration).
        const { id, ...userWithoutId } = session.user
        const profile = await createMember(
          session.user.id,
          { ...userWithoutId, onboardedAt: new Date() } as unknown as Partial<User>
        )
        setUserProfile(profile)

        setShowAuthorizationModal(false)
        setIsProfileComplete(true)
        setShowAuthorizationBanner(false)
        // setShowAdditionalInfoModal(true)
        toast('Additional Information Updated', 'success')
      } catch (error) {
        toast('Error creating member profile', 'error')
      }
    }
  }, [session?.user])

  // Auto-confirm for hackathon-provisioned accounts
  useEffect(() => {
    if (isHackathonProvisioned && session?.user && !userInfo) {
      handleConfirmation()
    }
  }, [isHackathonProvisioned, session?.user, userInfo, handleConfirmation])

  const handleAdditionalInfoSubmit = async (data: AdditionalInfoType) => {
    if (session?.user) {
      const updatedData = { ...data, onboardedAt: new Date() }
      try {
        await onboardProfile(session.user.id, updatedData)

        setShowAdditionalInfoModal(false)
        setShowAuthorizationBanner(false)
        setIsProfileComplete(true)

        toast('Additional Information Updated', 'success')
      } catch (error) {
        console.error('Error Updating User Profile:', error)
        toast('Error Updating User Profile', 'error')
      }
    }
  }

  // Ref guard: this effect calls setUserInfo, which re-renders every context
  // consumer including this component. Without the guard, any identity change
  // in the dependency array (a re-created context setter, a re-serialized
  // session prop after a server re-render) re-fires the effect, and each run
  // is a Server Action round-trip (POST to the current page URL) — i.e. an
  // infinite request loop. The profile only needs syncing once per completion.
  const hasSyncedProfileRef = useRef(false)
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (isProfileComplete && session?.user && !hasSyncedProfileRef.current) {
        hasSyncedProfileRef.current = true
        try {
          const newUserInfo = (await fetchUserProfile(session.user.id)) as User

          setUserInfo(newUserInfo)
        } catch (error) {
          console.error('Error fetching user profile:', error)
        }
      }
    }

    fetchUserInfo()
  }, [isProfileComplete, session?.user, setUserInfo])

  const closeModal = () => {
    setShowAuthorizationModal(false)
    setShowAdditionalInfoModal(false)
  }

  const showModal = () => {
    if (!userProfile) {
      setShowAuthorizationModal(true)
    } else if (!userProfile.onboardedAt) {
      setShowAdditionalInfoModal(true)
    }
  }

  return (
    <>
      {showAuthorizationBanner && (
        <AuthorizationBanner handleUpdateProfile={showModal} />
      )}
      {showAuthorizationModal && (
        <AuthorizationModal
          session={session}
          confirm={handleConfirmation}
          closeModal={closeModal}
        />
      )}
      {showAdditionalInfoModal && (
        <AdditionalInfoModal
          submit={handleAdditionalInfoSubmit}
          closeModal={closeModal}
        />
      )}
    </>
  )
}

export default AuthorizationContainer
